import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAppContext } from '../../contexts/AppContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 1200; // Higher than DetailModal (usually 1000-1100)
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease;
  box-shadow: 0 -4px 30px rgba(0,0,0,0.15);
`;

const Header = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f1f1;
`;

const Title = styled.h2`
  font-size: 1rem;
  font-weight: 800;
  color: #e74c3c; // Red for report
  margin: 0;
`;

const CloseButton = styled.button`
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6B7280;
`;

const Content = styled.div`
  padding: 20px;
  overflow-y: auto;
  padding-bottom: 40px;
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const ReasonItem = styled.label`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${props => props.selected ? '#e74c3c' : '#E5E7EB'};
  background: ${props => props.selected ? '#fff5f5' : 'white'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
`;

const Radio = styled.input`
  margin-right: 12px;
  accent-color: #e74c3c;
  transform: scale(1.2);
`;

const ReasonText = styled.span`
  font-size: 0.95rem;
  color: #374151;
  font-weight: 500;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: #e74c3c;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:disabled {
    background: #E5E7EB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const ReportModal = ({ isOpen, onClose, spotId }) => {
    const { t } = useTranslation();
    const { environment } = useAppContext();
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    // Common report reasons
    const reasons = [
        { id: 'spam', label: t('report_reason_spam', 'Spam / Advertising') },
        { id: 'inappropriate', label: t('report_reason_inappropriate', 'Inappropriate Content') },
        { id: 'abusive', label: t('report_reason_abusive', 'Abusive / Harassment') },
        { id: 'other', label: t('report_reason_other', 'Other') }
    ];

    const handleSubmit = async () => {
        if (!reason) {
            alert(t('report_alert_select_reason', 'Please select a reason'));
            return;
        }

        if (!window.confirm(t('report_confirm_submit', 'Are you sure you want to report this post?'))) {
            return;
        }

        setLoading(true);
        try {
            const reportSpot = httpsCallable(functions, 'reportSpot');
            const response = await reportSpot({
                spotId,
                reason,
                description,
                environment
            });

            if (response.data.success) {
                alert(t('report_success', 'Report submitted successfully.'));
                onClose();
                // Maybe trigger a local refetch or hide the spot locally via callback?
                // But for now, we just close.
            }
        } catch (error) {
            console.error('Report error:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose} data-modal-open="true">
            <ModalContainer onClick={e => e.stopPropagation()}>
                <Header>
                    <Title>{t('report_title', 'Report Content')}</Title>
                    <CloseButton onClick={onClose}><IoClose size={20} /></CloseButton>
                </Header>
                <Content>
                    <ReasonList>
                        {reasons.map(r => (
                            <ReasonItem key={r.id} selected={reason === r.id}>
                                <Radio
                                    type="radio"
                                    name="reportReason"
                                    value={r.id}
                                    checked={reason === r.id}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                                <ReasonText>{r.label}</ReasonText>
                            </ReasonItem>
                        ))}
                    </ReasonList>

                    <TextArea
                        placeholder={t('report_desc_placeholder', 'Additional details (optional)...')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    <SubmitButton onClick={handleSubmit} disabled={loading}>
                        {loading ? t('report_submitting', 'Submitting...') : t('report_submit', 'Submit Report')}
                    </SubmitButton>
                </Content>
            </ModalContainer>
        </Overlay>
    );
};

export default ReportModal;
